import { useState, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import { ReactComponent as RealtimeCalcIcon } from '../../../image/RealtimeCalcIcon.svg';

export default function LTVMainContent(props) {
  const {
    priceAvg,
    durationAvg,
    durationMembers,
    startDate,
    onChange,
    membersLabel,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [revenueAvg, setRevenueAvg] = useState(priceAvg);
  const [members, setMembers] = useState(durationMembers.length);
  const [duration, setDuration] = useState(durationAvg);

  const [isDragging, setIsDragging] = useState(false);

  const sliderMaxRef = useRef({
    revenueAvg: priceAvg * 2,
    members: durationMembers.length * 2,
    duration: durationAvg * 2,
  });
  const draggingRef = useRef(null);

  const revenueAvgTrackRef = useRef(null);
  const membersTrackRef = useRef(null);
  const durationTrackRef = useRef(null);

  // 슬라이더 최댓값: props 초기값의 2배 (매 렌더마다 ref 동기화) //
  const sliderMax = {
    revenueAvg: priceAvg * 2,
    members: durationMembers.length * 2,
    duration: durationAvg * 2,
  };

  sliderMaxRef.current = sliderMax;

  const trackRefMap = {
    revenueAvg: revenueAvgTrackRef,
    members: membersTrackRef,
    duration: durationTrackRef,
  };

  const setterMap = {
    revenueAvg: setRevenueAvg,
    members: setMembers,
    duration: setDuration,
  };

  // props 변경 시 매출 state 동기화 //
  useEffect(() => {
    setRevenueAvg(priceAvg);
  }, [priceAvg]);

  // props 변경 시 회원 수 state 동기화 //
  useEffect(() => {
    setMembers(durationMembers.length);
  }, [durationMembers]);

  // props 변경 시 지속 기간 state 동기화 //
  useEffect(() => {
    setDuration(durationAvg);
  }, [durationAvg]);

  // 슬라이더 드래그 이벤트 등록 (Pointer Events로 마우스/터치/펜 통합 처리) //
  useEffect(() => {
    const onPointerMove = (e) => {
      if (!draggingRef.current) return;

      const value = calcValueFromPointer(e.clientX, draggingRef.current);

      setterMap[draggingRef.current](value);
    };

    const onPointerEnd = () => {
      draggingRef.current = null;
      setIsDragging(false);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerEnd);
    document.addEventListener('pointercancel', onPointerEnd);

    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerEnd);
      document.removeEventListener('pointercancel', onPointerEnd);
    };
  }, []);

  // 부모 컴포넌트에 실시간 계산 결과 전달 (BottomCardRow 등 외부 영역에서 사용) //
  useEffect(() => {
    if (!onChange) return;

    const revenueAvgPerMember = (members === 0 || revenueAvg === 0) ? 0 : Math.round(revenueAvg / members);
    const ltv = (members === 0 || revenueAvg === 0 || duration === 0) ? 0 : Math.round((revenueAvg / members) * duration);
    const maxAdSpend = Math.round(ltv * 0.2);
    const totalRevenue = ltv * members;

    onChange({ revenueAvg, members, duration, ltv, revenueAvgPerMember, maxAdSpend, totalRevenue });
  }, [revenueAvg, members, duration, onChange]);

  // 포인터 위치를 슬라이더 값으로 환산 //
  function calcValueFromPointer(clientX, type) {
    const track = trackRefMap[type].current;

    if (!track) return 0;

    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const rawValue = ratio * sliderMaxRef.current[type];

    if (type === 'duration') return Math.round(rawValue * 10) / 10;

    return Math.round(rawValue);
  }

  // 슬라이더 드래그 시작 (Pointer Events로 마우스/터치/펜 통합 처리) //
  function onPointerDownSlider(type) {
    return (e) => {
      e.preventDefault();
      draggingRef.current = type;
      setIsDragging(true);

      const value = calcValueFromPointer(e.clientX, type);

      setterMap[type](value);
    };
  }

  // 초기값 복원 버튼 클릭 //
  function onClickResetButton() {
    setRevenueAvg(priceAvg);
    setMembers(durationMembers.length);
    setDuration(durationAvg);
  }

  // LTV 계산 //
  function calcLTV() {
    if (members === 0 || revenueAvg === 0 || duration === 0) return 0;

    return Math.round((revenueAvg / members) * duration);
  }

  // 권장 최대 광고비 계산 //
  function calcMaxAdSpend() {
    return Math.round(calcLTV() * 0.2);
  }

  // 슬라이더 위치 비율 계산 //
  function calcSliderPosition(value, max) {
    if (max === 0) return 0;

    const ratio = Math.min(value / max, 1);

    return ratio * 100;
  }

  const ltv = calcLTV();
  const maxAdSpend = calcMaxAdSpend();

  return (
    <MainContentRow>
      {/* 왼쪽: LTV 큰 값 + 계산식 */}
      <LeftSection>
        <LTVLabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {t('statistics.duration.totalRevenuePerCustomer')}
          </Text>
        </LTVLabelWrapper>
        <LTVValueWrapper>
          {
            t('statistics.duration.currencyPrefix') &&
            <Text fontFamily='NotoSansKR-Regular' fontSize={20} color={theme.text}>
              {t('statistics.duration.currencyPrefix')}
            </Text>
          }
          <LTVBigValueText fontFamily='NotoSansKR-Bold' fontSize={70} color={theme.brand500}>
            {ltv.toLocaleString('ko-KR')}
          </LTVBigValueText>
          {
            t('statistics.duration.currencySuffix') &&
            <Text fontFamily='NotoSansKR-Regular' fontSize={20} color={theme.text} style={{ alignSelf: 'flex-end' }}>
              {t('statistics.duration.currencySuffix')}
            </Text>
          }
        </LTVValueWrapper>
        <RecommendedAdWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {t('statistics.duration.recommendedAdSpendBefore')}
          </Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.brand500}>
            {t('statistics.duration.currencyPrefix') ? t('statistics.duration.currencyPrefix') + ' ' + maxAdSpend.toLocaleString('ko-KR') : maxAdSpend.toLocaleString('ko-KR') + t('statistics.duration.currencySuffix')}
          </Text>
          {
            t('statistics.duration.recommendedAdSpendAfter') &&
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('statistics.duration.recommendedAdSpendAfter')}
            </Text>
          }
        </RecommendedAdWrapper>

        {/* 계산식 박스 */}
        <FormulaBox backgroundColor={theme.buttonFill}>
          <FormulaCell>
            <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.text}>
              {revenueAvg.toLocaleString('ko-KR')}
            </Text>
            <FormulaDivider backgroundColor={theme.text} />
            <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.text}>
              {members.toLocaleString('ko-KR')}
            </Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>
              {startDate.format(t('statistics.dateFormatYearShort')) + ' ' + t('statistics.duration.formulaLabel')}
            </Text>
          </FormulaCell>
          <FormulaOperator>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.line}>x</Text>
          </FormulaOperator>
          <FormulaCell>
            <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.text}>
              {duration.toLocaleString('ko-KR')}
            </Text>
            <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>
              {t('statistics.duration.formulaMonths')}
            </Text>
          </FormulaCell>
          <FormulaOperator>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.line}>=</Text>
          </FormulaOperator>
          <FormulaResultCell>
            <FormulaResultValueRow>
              {
                t('statistics.duration.currencyPrefix') &&
                <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.text}>
                  {t('statistics.duration.currencyPrefix')}
                </Text>
              }
              <Text fontFamily='NotoSansKR-Bold' fontSize={18} color={theme.brand500}>
                {ltv.toLocaleString('ko-KR')}
              </Text>
              {
                t('statistics.duration.currencySuffix') &&
                <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.text}>
                  {t('statistics.duration.currencySuffix')}
                </Text>
              }
            </FormulaResultValueRow>
            <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>
              LTV
            </Text>
          </FormulaResultCell>
        </FormulaBox>
      </LeftSection>

      {/* 오른쪽: 슬라이더 3개 */}
      <RightSection>
        <BadgeRow>
          <RealtimeButton onClick={onClickResetButton}>
            <StyledRealtimeCalcIcon width={16} height={16} $color={theme.subBackground} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground} style={{ cursor: 'pointer' }}>
              {t('statistics.duration.realtimeLtvCalc')}
            </Text>
          </RealtimeButton>
        </BadgeRow>
        <SliderRow>
          <SliderLabel>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {startDate.format(t('statistics.dateFormatYearShort')) + ' ' + t('statistics.duration.monthlyAvgSales')}
            </Text>
          </SliderLabel>
          <SliderValue>
            {
              t('statistics.duration.currencyPrefix') &&
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
                {t('statistics.duration.currencyPrefix')}
              </Text>
            }
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
              {revenueAvg.toLocaleString('ko-KR')}
            </Text>
            {
              t('statistics.duration.currencySuffix') &&
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
                {t('statistics.duration.currencySuffix')}
              </Text>
            }
          </SliderValue>
        </SliderRow>
        <SliderTrackWrapper
          ref={trackRefMap.revenueAvg}
          onPointerDown={onPointerDownSlider('revenueAvg')}
          $isDragging={isDragging}>
          <SliderTrack backgroundColor={theme.line} />
          <SliderThumb
            left={calcSliderPosition(revenueAvg, sliderMax.revenueAvg)}
            borderColor={theme.brand500}
            backgroundColor={theme.subBackground}
            $isDragging={isDragging && draggingRef.current === 'revenueAvg'} />
        </SliderTrackWrapper>

        <SliderRow>
          <SliderLabel>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {membersLabel || t('statistics.duration.validMembers')}
            </Text>
          </SliderLabel>
          <SliderValue>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
              {members.toLocaleString('ko-KR')}
            </Text>
            <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
              {t('statistics.duration.unitMembers')}
            </Text>
          </SliderValue>
        </SliderRow>
        <SliderTrackWrapper
          ref={trackRefMap.members}
          onPointerDown={onPointerDownSlider('members')}
          $isDragging={isDragging}>
          <SliderTrack backgroundColor={theme.line} />
          <SliderThumb
            left={calcSliderPosition(members, sliderMax.members)}
            borderColor={theme.brand500}
            backgroundColor={theme.subBackground}
            $isDragging={isDragging && draggingRef.current === 'members'} />
        </SliderTrackWrapper>

        <SliderRow>
          <SliderLabel>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
              {t('statistics.duration.avgMembershipDuration')}
            </Text>
          </SliderLabel>
          <SliderValue>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
              {duration.toLocaleString('ko-KR')}
            </Text>
            <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>
              {t('statistics.duration.unitMonths')}
            </Text>
          </SliderValue>
        </SliderRow>
        <SliderTrackWrapper
          ref={trackRefMap.duration}
          onPointerDown={onPointerDownSlider('duration')}
          $isDragging={isDragging}>
          <SliderTrack backgroundColor={theme.line} />
          <SliderThumb
            left={calcSliderPosition(duration, sliderMax.duration)}
            borderColor={theme.brand500}
            backgroundColor={theme.subBackground}
            $isDragging={isDragging && draggingRef.current === 'duration'} />
        </SliderTrackWrapper>
      </RightSection>
    </MainContentRow>
  );
}

const MainContentRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 40px;

  width: 100%;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 30px 0;

  gap: 10px;

  width: calc(50% - 10px);
`;

const LTVLabelWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const LTVValueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const LTVBigValueText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  line-height: ${props => props.fontSize}px;

  color: ${props => props.color};
  margin: 0;
  cursor: default;
`;

const RecommendedAdWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  padding: 10px 0;
  gap: 4px;
`;

const FormulaBox = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  width: 100%;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  overflow: hidden;
`;

const FormulaCell = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 10px;
  height: 105px;

  border-radius: 10px;
`;

const FormulaDivider = styled.div`
  width: 100%;
  height: 1px;
  margin: 2px 0;

  border-radius: 100px;
  background-color: ${props => props.backgroundColor};
`;

const FormulaOperator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

const FormulaResultCell = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 0 10px;

  border-radius: 10px;
`;

const FormulaResultValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const StyledRealtimeCalcIcon = styled(RealtimeCalcIcon)`
  path {
    stroke: ${props => props.$color};
  }
`;

const BadgeRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  width: 100%;
  padding: 5px 0;
`;

const RealtimeButton = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  width: auto;
  height: auto;

  padding: 0 5px;
  gap: 2px;

  border: 5px;
  border-radius: 5px;
  background-color: ${props => props.theme.brand600};
  cursor: pointer;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: calc(50% + 10px);
`;

const SliderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  height: 50px;
  width: 100%;
`;

const SliderLabel = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const SliderValue = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 5px;
`;

const SliderTrackWrapper = styled.div`
  position: relative;

  display: flex;
  align-items: center;

  width: 100%;
  height: 30px;

  cursor: pointer;
  user-select: none;
  touch-action: none;
`;

const SliderTrack = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${props => props.backgroundColor};
`;

const SliderThumb = styled.div`
  position: absolute;
  left: ${props => props.left}%;

  box-sizing: border-box;

  width: 22px;
  height: 22px;

  border: 4px solid ${props => props.borderColor};
  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};

  transform: translateX(-50%);
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;
